import { useState } from "react";
import { useNavigate } from "react-router-dom";

import useStateHandler from "@/hooks/useStateHandler";
import useTranslations from '@/i18n/useTranslations';
import GalleryService from "@/services/gallery.service";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import GoBackButton from "@/shared-components/buttons/components/GoBackButton";
import ArticleInfo from '@/shared-components/forms/add-board-content/add-article/published-article/ArticleInfo';
import AudienceSelectionPopup from '@/shared-components/forms/add-board-content/shared-components/AudienceSelectionPopup';
import Utils from '@/utils';
import { buildPublicContentPath } from '@/utils/contentRoutes';

import {
  applyPublicationAction,
  PUBLICATION_ACTIONS,
} from '../../publicationLifecycle';
import { getArticlePayload, hasMeaningfulPostText } from '../../saveFunctions';
import Cover from "../cover";

import "@/shared-components/forms/add-board-content/add-article/inside-header/index.css";
import "./ArticlePreview.css";

export default function ArticlePreview({
  formState,
  onClose,
  setFormError,
  membersAudienceEnabled = false
}) {
  const goBackHandler = onClose;
  const navigate = useNavigate();
  const t = useTranslations('common.forms.board_editor');
  const { cacheHandler, stateHandler } = useStateHandler();
  const [loading, setLoading] = useState(false);
  const [openAudienceSelection, setOpenAudienceSelection] = useState(false);

  // we get the structure to ensure that we have a body text
  const tempPayload = getArticlePayload(formState);

  // ─── mirror the header’s publish‐enabled logic ───
  const hasTitle  = Boolean(tempPayload.post_title?.trim());
  const hasDesc   = Boolean(tempPayload.post_description?.trim());
  const hasCover  = Boolean(tempPayload.post_cover);
  // check every block type
  const hasText     = hasMeaningfulPostText(tempPayload.post_texts);
  const hasPictures = (tempPayload.post_pictures || []).length > 0;
  const hasArt      = (tempPayload.post_artworks || []).length > 0;
  const hasVideos   = (tempPayload.post_videos || []).length > 0;
  const hasPolls    = (tempPayload.post_polls || []).length > 0;
  const hasQuizzes  = (tempPayload.post_quizzes || []).length > 0;

  const hasBody     = hasText || hasPictures || hasArt || hasVideos || hasPolls || hasQuizzes;

  // publish enabled only once everything is present
  const canPublish  = hasTitle && hasCover && hasDesc && hasBody;

  // TODO: This funciton is copied from  add-article/inside-header/index.jsx
  // consider to move it to a common utils file
  async function savePost({ audience } = {}) {
    setLoading(true);
    setFormError(null);

    // TODO: Pablo, show an error?
    // This is when they are uploading the cover picture
    // and still in process
    if (Utils.isPostMediaUploading(formState)) { 
      setLoading(false);
      return;
    }
    if (Utils.isPostVideoProcessing(formState)) {
      setLoading(false);
      stateHandler.set('temporalPopup', {
        text: t('video_quality_processing'),
        variant: 'error',
      });
      return;
    }

    const payload = applyPublicationAction(
      getArticlePayload(formState),
      PUBLICATION_ACTIONS.PUBLISH
    );

    payload.audience = audience;

    const { response, data } = await GalleryService.submitPost(payload, true);
    setLoading(false);

    if (response.ok) {
      // TODO: Check if it is needed
      cacheHandler.triggerAction("SUBMIT_POST");
      cacheHandler.storeInCache(`post_${data._id}_view`, data);
      navigate(
        buildPublicContentPath(data, 'article'),
        {
          replace: true,
          // no more “from” — this means the back-button will fall back
          // to your board route in one click
          state: { }
        }
      );
    } else if (response.status === 400) {
      setFormError({
        element: data.error_data.element,
        error_code: data.error_type
      });
    } else if (data?.error_type === 'VIDEO_PROCESSING_INCOMPLETE') {
      stateHandler.set('temporalPopup', {
        text: t('video_quality_processing'),
        variant: 'error',
      });
    }
  }

  const handlePublish = () => {
    if (membersAudienceEnabled) {
      setOpenAudienceSelection(true);
      return;
    }

    savePost({ audience: 'public' });
  };

  const handleAudienceSelect = audience => {
    savePost({ audience });
  };
  
  return (
    <div className="article__preview-container">
      {/* Header */}
      <header className="board-header article__preview-header">
        <GoBackButton inline onClick={goBackHandler} />
        <h1 className="board-header__heading article__preview-header__title">Preview</h1>
        <div className="board-header__right-options">
          {!formState._id && (
            <ContinueButton
              className="board-header__item publish element-non-selectable board-header__continue-small"
              turnOff={!canPublish}
              loading={loading}
              onClick={handlePublish}
            >
              Post
            </ContinueButton>
          )}
        </div>
      </header>

      {/* Article Content */}
      <main className="article__preview-article">
        <h2 className="article__preview-article__title">{formState.post_title}</h2>
        <p className="article__preview-article__description">{formState.post_description}</p>
        <div className="article__preview-article__cover">
          <Cover 
            formState={formState}
            setFormState={()=>{}}
            element="post_cover"
            formError={null}
            previewMode={true}   // Pass previewMode flag
          />
        </div>

        <div className="article__content">
          {/* 1) ArticleInfo (renders body + footer) */}
          <ArticleInfo
            fetchData={{ post_container: tempPayload }}
            likeCount={0}
            isLiked={false}
            onLike={undefined}
            commentCount={0}
            onOpenComments={0}
            initialSlideIndex={0}
            isPreviewMode={true}
          />
        </div>
      </main>

      <AudienceSelectionPopup
        open={openAudienceSelection}
        onClose={() => setOpenAudienceSelection(false)}
        onSelect={handleAudienceSelect}
      />
    </div>
  );
}
